import {
  buildMetadataExample,
  buildSubSamplePayloadExample,
  CopyIcon,
  type ContractAnalysisRow,
  type LabRequisition,
  ModalDialog,
  Mtds,
  TestTubeIcon
} from 'prove-common'
import { useMemo, useState } from 'react'

// ---------------------------------------------------------------------------
// Test requisition modal
// ---------------------------------------------------------------------------

const TEST_REQUISITION_MODAL_ID = 'test-requisition-modal'

type TestRequisitionModalProps = {
  requisition: LabRequisition
  isOpen: boolean
  setOpen: (open: boolean) => void
}

const TestRequisitionModal = ({
  requisition,
  isOpen,
  setOpen
}: TestRequisitionModalProps) => {
  const [copied, setCopied] = useState(false)
  const json = JSON.stringify(requisition, null, 2)

  const copy = () => {
    navigator.clipboard.writeText(json).then(() => {
      setCopied(true)
      setTimeout(() => setCopied(false), 2000)
    })
  }

  const download = () => {
    const blob = new Blob([json], { type: 'application/json' })
    const url = URL.createObjectURL(blob)
    const link = document.createElement('a')
    link.href = url
    link.download = `test-requisition-${requisition.sampleId}.json`
    document.body.appendChild(link)
    link.click()
    link.remove()
    URL.revokeObjectURL(url)
  }

  return (
    <ModalDialog
      id={TEST_REQUISITION_MODAL_ID}
      closedby='any'
      open={isOpen}
      onClose={() => setOpen(false)}
      data-color='main'
    >
      <Mtds.Button
        command='close'
        commandfor={TEST_REQUISITION_MODAL_ID}
        aria-label='Close'
      />
      <Mtds.Prose>
        <Mtds.Heading>Test requisition</Mtds.Heading>
        <Mtds.Flex data-gap='2'>
          <Mtds.Button
            key={copied ? 'copied' : 'default'}
            data-variant='secondary'
            data-size='sm'
            onClick={copy}
          >
            <CopyIcon />
            {copied ? 'Copied!' : 'Copy JSON'}
          </Mtds.Button>
          <Mtds.Button
            data-variant='secondary'
            data-size='sm'
            onClick={download}
          >
            Download JSON
          </Mtds.Button>
        </Mtds.Flex>
        <pre className='overflow-x-auto'>{json}</pre>
      </Mtds.Prose>
    </ModalDialog>
  )
}

// ---------------------------------------------------------------------------
// Test requisition section (card + modal trigger)
// ---------------------------------------------------------------------------

export type TestRequisitionSectionProps = {
  row: ContractAnalysisRow
  contractTitle: string | undefined
}

export const TestRequisitionSection = ({
  row,
  contractTitle
}: TestRequisitionSectionProps) => {
  const [isOpen, setOpen] = useState(false)
  const { analysisType } = row
  const at = analysisType.analysisType

  const requisition: LabRequisition = useMemo(() => {
    const now = new Date().toISOString()
    const metadata = buildMetadataExample(at?.payloadSchema?.schema)
    const subSamplePayload = buildSubSamplePayloadExample(
      at?.payloadSchema?.schema
    )
    const substances = (at?.substances ?? [])
      .filter((s) => s.substance != null)
      .map((s) => ({
        id: s.substance.id,
        name: s.substance.name,
        code: s.substance.code
      }))
    return {
      id: 99999,
      sampleId: 'TEST-000001',
      sampleType: {
        id: at?.sampleType?.id ?? 0,
        matrixId: at?.sampleType?.matrixId ?? 0,
        matrix: at?.sampleType?.matrix ?? '',
        matrixCode: at?.sampleType?.matrixCode ?? '',
        productId: at?.sampleType?.productId ?? 0,
        product: at?.sampleType?.product ?? '',
        productCode: at?.sampleType?.productCode ?? ''
      },
      substances,
      reportedSubstanceCount: 0,
      totalSubstanceCount: substances.length,
      pendingSubstanceCodes: substances.map((s) => s.code),
      contractRef: contractTitle ?? 'TEST-CONTRACT-REF',
      status: 'created',
      createdDate: now,
      takenDate: now,
      subSamples: [
        {
          id: 1,
          subSampleId: 1,
          ...(subSamplePayload != null && { payload: subSamplePayload })
        }
      ],
      ...(metadata != null && { metadata })
    }
  }, [at, contractTitle])

  return (
    <Mtds.Card
      as={Mtds.Grid}
      className='bg-surface-tinted'
      data-color='neutral'
    >
      <p>
        Generate a synthetic requisition JSON for this combination. Values are
        populated from the schema&apos;s examples and enums, making it useful
        for verifying your parsing logic before real requisitions arrive.
      </p>
      <div>
        <Mtds.Button data-variant='secondary' onClick={() => setOpen(true)}>
          <TestTubeIcon />
          Generate test requisition
        </Mtds.Button>
        {isOpen && (
          <TestRequisitionModal
            requisition={requisition}
            isOpen={isOpen}
            setOpen={setOpen}
          />
        )}
      </div>
    </Mtds.Card>
  )
}
